import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { apiRequest } from '@/src/services';
import { useDictionary, usePage } from '@/src/app/hooks';
import { Loading, ErrorMessage, Container, type UnitDTO, type ResourceHeaderDTO } from '@/src/ui';
import {
    filterUnitsData,
    filterAssessmentsData,
    filterResourcesData,
    filterLanguageSetting,
} from '@/src/utils';
import './LessonsPage.css';
import CollapsibleUnit from './components/CollapsibleUnit';

const LessonsPage: React.FC = () => {
    const { translate } = useDictionary();
    const { setSubheaderTitle, setSubheaderType, pageGrade, setPageGrade, pageLanguage } =
        usePage();
    document.title = translate('All Lessons', 'All Lessons');

    const params = useParams();
    const grade = params.grade || pageGrade;
    useEffect(() => {
        if (grade !== undefined && grade !== pageGrade) {
            setPageGrade(grade);
        }
    }, [grade, pageGrade, setPageGrade]);

    const [unitsData, setUnitsData] = useState<UnitDTO[] | null>(null);
    const [assessmentsData, setAssessmentsData] = useState<ResourceHeaderDTO[] | null>(null);
    const [resourcesData, setResourcesData] = useState<ResourceHeaderDTO[] | null>(null);
    const [lessonsError, setLessonsError] = useState('');
    const [assessmentsError, setAssessmentsError] = useState('');
    const [resourcesError, setResourcesError] = useState('');
    const [loading, setLoading] = useState(false);

    const [selectedUnit, setSelectedUnit] = useState<number | null>(
        params.unit ? parseInt(params.unit) : null
    );
    const [selectedLessonIndex, setSelectedLessonIndex] = useState<number | null>(
        params.lesson ? parseInt(params.lesson) : null
    );
    const [selectedTab, setSelectedTab] = useState<'lessons' | 'assessments' | 'resources' | null>(
        null
    );

    useEffect(() => {
        setSubheaderTitle(translate('Grade') + ' ' + grade);
        setSubheaderType('hero');
        setPageGrade(grade);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translate, grade]);

    // Allows grade to load from dashboard MyLessons without clearing selected values
    const loadedGrade = useRef(grade);
    useEffect(() => {
        if (loadedGrade.current == grade) {
            return;
        }
        loadedGrade.current = grade;
        setSelectedUnit(null);
        setSelectedLessonIndex(null);
        setSelectedTab(null);
    }, [grade]);

    useEffect(() => {
        (async function fetchData() {
            setLessonsError('');
            setAssessmentsError('');
            setResourcesError('');
            setLoading(true);
            setUnitsData(null);
            setAssessmentsData(null);
            setResourcesData(null);
            const language = filterLanguageSetting(grade, pageLanguage);
            const [unitsResult, assessmentsResult, resourcesResult] = await Promise.allSettled([
                apiRequest<UnitDTO[]>('GET', `dbo/lessons?grade=${grade}&language=${language}`),
                apiRequest<ResourceHeaderDTO[]>(
                    'GET',
                    `dbo/assessments?grade=${grade}&language=${language}`
                ),
                apiRequest<ResourceHeaderDTO[]>(
                    'GET',
                    `dbo/resources?grade=${grade}&language=${language}`
                ),
            ]);

            if (unitsResult.status === 'fulfilled') {
                setUnitsData(filterUnitsData(grade, unitsResult.value.data));
            } else {
                const error = unitsResult.reason as { status?: number };
                if (error.status === 403) {
                    setLessonsError('You do not have permission to view this grade.');
                } else if (error.status === 404) {
                    setLessonsError('Lessons data could not be found at this time.');
                } else {
                    setLessonsError('Failed to fetch Lessons data.');
                }
            }

            if (assessmentsResult.status === 'fulfilled') {
                setAssessmentsData(
                    filterAssessmentsData(assessmentsResult.value.data, grade, pageLanguage)
                );
            } else {
                const error = assessmentsResult.reason as { status?: number };
                if (error.status === 403) {
                    setAssessmentsError('You do not have permission to view this grade.');
                } else if (error.status === 404) {
                    setAssessmentsError('Assessments data could not be found at this time.');
                } else {
                    setAssessmentsError('Failed to fetch Assessments data.');
                }
            }

            if (resourcesResult.status === 'fulfilled') {
                setResourcesData(
                    filterResourcesData(resourcesResult.value.data, grade, pageLanguage)
                );
            } else {
                const error = resourcesResult.reason as { status?: number };
                if (error.status === 403) {
                    setResourcesError('You do not have permission to view this grade.');
                } else if (error.status === 404) {
                    setResourcesError('Resources data could not be found at this time.');
                } else {
                    setResourcesError('Failed to fetch Resources data.');
                }
            }

            setLoading(false);
        })();
    }, [grade, pageLanguage]);

    return (
        <Container>
            <ErrorMessage error={lessonsError} />
            {loading ? (
                <Loading />
            ) : (
                <div id="unitsContainer">
                    {unitsData
                        ? unitsData.map((unit: UnitDTO, index: number) => {
                              const key = `lessons-unit-${grade}-${index}`;
                              return (
                                  <CollapsibleUnit
                                      key={key}
                                      loading={loading}
                                      assessmentsError={assessmentsError}
                                      resourcesError={resourcesError}
                                      assessmentsData={assessmentsData}
                                      resourcesData={resourcesData}
                                      unit={unit}
                                      index={index}
                                      selectedUnit={selectedUnit}
                                      selectedLessonIndex={selectedLessonIndex}
                                      selectedTab={selectedTab}
                                      setSelectedUnit={setSelectedUnit}
                                      setSelectedTab={setSelectedTab}
                                      setSelectedLessonIndex={setSelectedLessonIndex}
                                  />
                              );
                          })
                        : null}
                </div>
            )}
        </Container>
    );
};

export default LessonsPage;
